import { Alert, BasicInput, palette } from '@dev/ee-components';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { useGetCampaignInfo } from '@/components/CampaignCreator/hooks/useGetCampaignInfo';
import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
import { usePilotPlanConfig } from '@/services/hooks/usePilotPlanConfig';
import { getCampainCreatorStore, ModifyCampaignSendTest } from '@/store/campaignCreator/actions';
import { CampaignErrorNames } from '@/store/campaignCreator/types';

const ContentWrapper = styled.div`
    & > label {
        margin-top: 16px;
    }
    & > label:first-of-type {
        margin-top: 0;
    }
`;

const AlertWrapper = styled.div`
    .MuiPaper-root {
        background-color: ${palette.red[1]} !important;
        padding: 6px 16px !important;
        margin-bottom: 16px;
    }
`;

export const SendTestModal = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isPilotPlan } = useCurrentPlanStatus();
    const { errors } = useSelector(getCampainCreatorStore);
    const { fromEmail, fromName, subject, previewText, replyEmail, replyName } = useGetCampaignInfo();
    const [toEmailError, setToEmailError] = useState(false);
    const { sendToInput } = usePilotPlanConfig();

    const sendTestCreditsHasError = !!errors?.[CampaignErrorNames.NO_EMAIL_CREDITS];
    const sendTestSizeHasError = !!errors?.[CampaignErrorNames.SEND_TEST_TEMPLATE_SIZE];
    const sendTestInvalidEmailsError = !!errors?.[CampaignErrorNames.SEND_TEST_INVALID_EMAILS];
    const sendTestInvalidEmailsStatusError = !!errors?.[CampaignErrorNames.SEND_TEST_INVALID_EMAILS_STATUS];

    const sendToPilotPlanConfig = isPilotPlan && sendToInput;

    useEffect(() => {
        dispatch(ModifyCampaignSendTest('to', isPilotPlan ? sendToPilotPlanConfig?.defaultValue : fromEmail));
        dispatch(ModifyCampaignSendTest('fromName', fromName));
        dispatch(ModifyCampaignSendTest('subject', subject));
        dispatch(ModifyCampaignSendTest('replyEmail', replyEmail));
        dispatch(ModifyCampaignSendTest('replyName', replyName));
        dispatch(ModifyCampaignSendTest('previewText', previewText));
    }, []);

    useEffect(() => {
        setToEmailError(sendTestInvalidEmailsError || sendTestInvalidEmailsStatusError);
    }, [errors]);

    const setToEmail = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (toEmailError) {
            setToEmailError(false);
        }
        dispatch(ModifyCampaignSendTest('to', event.target.value));
    };

    const setFromName = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        dispatch(ModifyCampaignSendTest('fromName', event.target.value));
    };

    const setSubject = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        dispatch(ModifyCampaignSendTest('subject', event.target.value));
    };

    const setPreviewText = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        dispatch(ModifyCampaignSendTest('previewText', event.target.value));
    };

    const toInputErrorText = sendTestInvalidEmailsStatusError ? t('emailstatusinvalid') : t('emailinvalid');

    return (
        <ContentWrapper>
            <AlertWrapper>
                {sendTestCreditsHasError && <Alert severity="error" body={t('emailslimiterror')} />}
                {sendTestSizeHasError && <Alert severity="error" body={t('sendtemplatesizeerror')} />}
            </AlertWrapper>
            <BasicInput
                id="input-send-test-to"
                label={t('to')}
                fullWidth={true}
                defaultValue={fromEmail}
                onChange={setToEmail}
                error={toEmailError}
                errorMessage={toInputErrorText}
                descriptionMessage={t('toinputdescriptionmessage')}
                {...sendToPilotPlanConfig}
            />
            <BasicInput
                id="input-send-test-from-name"
                label={t('fromname')}
                defaultValue={fromName}
                onChange={setFromName}
            />
            <BasicInput
                id="input-send-test-subject"
                label={t('subject')}
                defaultValue={subject}
                onChange={setSubject}
            />
            <BasicInput
                id="input-send-test-preview-text"
                label={t('previewtext')}
                defaultValue={previewText}
                onChange={setPreviewText}
            />
        </ContentWrapper>
    );
};
